<script lang="ts">
	import * as Card from "#lib/web-ui/ui/card/index.js";
	import * as Tabs from "#lib/web-ui/ui/tabs/index.js";
	import CheckIcon from "@lucide/svelte/icons/check";
	import FileWarningIcon from "@lucide/svelte/icons/file-warning";
	import InfoIcon from "@lucide/svelte/icons/info";

	let language = $state("english");

	const englishContent = [
		"All payments requested must be of work completed by Tuesday at 5 PM.",
		"All payments requested must have a picture of an invoice for any work completed.",
		"All payments requested must have pictures of completed work.",
		"All payments requested must be submitted by the contractor directly.",
		"Any work not completed 100% in pictures submitted directly by contractor will have payment requests revised or declined per the agreed scope of work."
	];

	const spanishContent = [
		"Todos los pagos solicitados deben ser de trabajo completado antes del martes a las 5:00 p.m.",
		"Todos los pagos solicitados deben tener una imagen de una factura por cualquier trabajo completado.",
		"Todos los pagos solicitados deben tener imágenes del trabajo completado.",
		"Todos los pagos solicitados deben ser enviados directamente por el contratista.",
		"Cualquier trabajo que no esté completado al 100% en las imágenes enviadas directamente por el contratista tendrá solicitudes de pago revisadas o rechazadas."
	];
</script>

<svelte:head>
	<title>Vendor Payment Request | Fergmar</title>
	<meta
		name="description"
		content="Submit your vendor payment request quickly and securely through Fergmar’s online form."
	/>
	<meta
		name="keywords"
		content="vendor payment request, contractor payment request, contractor payment, contractor payment request form, Fergmar"
	/>
	<meta name="viewport" content="width=device-width, initial-scale=1.0" />
	<meta property="og:title" content="Vendor Payment Request | Fergmar" />
	<meta property="og:site_name" content="Fergmar" />
	<meta
		property="og:description"
		content="Submit your vendor payment request quickly and securely through Fergmar’s online form."
	/>
</svelte:head>

<div class="relative isolate overflow-hidden bg-linear-to-r from-violet-600 to-indigo-600">
	<div class="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
		<div class="mx-auto max-w-2xl text-center">
			<h2 class="text-4xl font-semibold tracking-tight text-balance text-white sm:text-5xl">
				Vendor Payment Request
			</h2>
			<p class="text-secondary mx-auto mt-6 max-w-xl text-lg/8 text-pretty">
				Submit your payment request online through our secure portal
			</p>
			<div class="mt-10 flex items-center justify-center gap-x-6"></div>
		</div>
	</div>
</div>

<!-- Main Content Section -->
<div class="overflow-hidden bg-white py-24 sm:py-32">
	<div class="mx-auto max-w-7xl px-6 lg:px-8">
		<!-- Language Selection Tabs -->

		<div class="grid grid-cols-1 gap-6 lg:grid-cols-12">
			<!-- Left sidebar: Payment information -->
			<div class="col-span-1 lg:col-span-5">
				<!-- Payment Requirements Card -->
				<div class="space-y-6">
					<Card.Root class="rounded border border-gray-200 shadow-sm">
						<Card.Header class="border-b border-gray-200 bg-gray-50 px-4 py-3">
							<Card.Title class="text-foreground text-base font-medium"
								>Payment Requirements</Card.Title
							>
						</Card.Header>
						<Card.Content class="p-4">
							<Tabs.Root
								value={language}
								onValueChange={(val) => (language = val)}
								class="w-full"
							>
								<Tabs.List class="grid grid-cols-2 rounded bg-gray-100 p-1">
									<Tabs.Trigger
										value="english"
										class="rounded py-2 text-sm font-medium data-[state=active]:bg-blue-600 data-[state=active]:text-white"
									>
										English
									</Tabs.Trigger>
									<Tabs.Trigger
										value="spanish"
										class="rounded py-2 text-sm font-medium data-[state=active]:bg-blue-600 data-[state=active]:text-white"
									>
										Español
									</Tabs.Trigger>
								</Tabs.List>
								<Tabs.Content value="english">
									<div class="space-y-3">
										<p class="text-sm text-gray-700">
											All work should be completed within the billing time
											period list above, in order to be eligible for payment
											in a given week the following criteria must be met:
										</p>
										<ul class="space-y-2">
											{#each englishContent as item}
												<li class="flex gap-x-2 text-sm text-gray-700">
													<CheckIcon
														class="mt-0.5 h-4 w-4 shrink-0 text-blue-600"
													/>
													<span>{item}</span>
												</li>
											{/each}
										</ul>
									</div>
								</Tabs.Content>

								<Tabs.Content value="spanish">
									<div class="space-y-3">
										<p class="text-sm text-gray-700">
											Todo el trabajo debe completarse dentro del período de
											facturación indicado anteriormente. Para ser elegible
											para el pago en una semana determinada, se deben cumplir
											los siguientes criterios:
										</p>
										<ul class="space-y-2">
											{#each spanishContent as item}
												<li class="flex gap-x-2 text-sm text-gray-700">
													<CheckIcon
														class="mt-0.5 h-4 w-4 shrink-0 text-blue-600"
													/>
													<span>{item}</span>
												</li>
											{/each}
										</ul>
									</div>
								</Tabs.Content>
							</Tabs.Root>
						</Card.Content>
					</Card.Root>

					<!-- Registration Info Card -->
					<Card.Root class="rounded border border-gray-200 shadow-sm">
						<Card.Header class="border-b border-gray-200 bg-gray-50 px-4 py-3">
							<Card.Title class="text-foreground text-base font-medium"
								>Registration Information</Card.Title
							>
						</Card.Header>
						<Card.Content class="p-4">
							<Tabs.Root value={language}>
								<Tabs.Content value="english">
									<div class="space-y-3">
										<h3 class="text-foreground text-sm font-medium">
											Don't see your Name in the List?
										</h3>
										<p class="text-sm text-gray-700">
											If you do not see your name, or your business name on
											file, you must first register with the company to be
											eligible for payment requests.
										</p>
										<p class="text-sm text-gray-700">
											The first step is to submit a W-9 along with proof of
											identification with either your matching social or EIN
											number to the superintendent who initially contracted
											you.
										</p>
										<p class="text-sm text-gray-700">
											Once the information has been registered and verified,
											payment requests can be made.
										</p>
										<a
											href="https://www.irs.gov/pub/irs-pdf/fw9.pdf"
											target="_blank"
											rel="noopener noreferrer"
											class="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
										>
											<FileWarningIcon class="mr-2 h-4 w-4" />
											Download W-9 Form (PDF)
										</a>
									</div>
								</Tabs.Content>
								<Tabs.Content value="spanish">
									<div class="space-y-3">
										<h3 class="text-foreground text-sm font-medium">
											¿No ve su nombre en la lista?
										</h3>
										<p class="text-sm text-gray-700">
											Si no ve su nombre o el nombre de su empresa en el
											archivo, primero debe registrarse con la empresa para
											ser elegible para las solicitudes de pago.
										</p>
										<p class="text-sm text-gray-700">
											El primer paso es enviar un W-9 junto con una prueba de
											identificación con su número social o EIN
											correspondiente al superintendente que lo contrató
											inicialmente.
										</p>
										<p class="text-sm text-gray-700">
											Una vez que la información ha sido registrada y
											verificada, se pueden realizar solicitudes de pago.
										</p>
										<a
											href="https://www.irs.gov/pub/irs-pdf/fw9.pdf"
											target="_blank"
											rel="noopener noreferrer"
											class="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
										>
											<FileWarningIcon class="mr-2 h-4 w-4" />
											Descargar formulario W-9 (PDF)
										</a>
									</div>
								</Tabs.Content>
							</Tabs.Root>
						</Card.Content>
					</Card.Root>
				</div>
			</div>

			<!-- Main content: Payment Request Form with Airtable Iframe -->
			<div class="col-span-1 lg:col-span-7">
				<Card.Root class="h-full rounded border border-gray-200 shadow-sm">
					<Card.Header class="border-b border-gray-200 bg-gray-50 px-4 py-3">
						<Card.Title class="text-foreground text-base font-medium"
							>Payment Request Form</Card.Title
						>
						<p class="mt-1 flex items-center text-xs text-gray-500">
							<InfoIcon class="mr-1.5 h-3.5 w-3.5" />
							You must be registered for Payments with Fergmar
						</p>
					</Card.Header>
					<Card.Content class="p-0">
						<!-- Airtable Embed with dynamic height -->
						<div class="w-full overflow-hidden">
							<iframe
								title="Payment Request Form"
								class="airtable-embed airtable-dynamic-height"
								src="https://airtable.com/embed/appA4krU6Rte6PEAH/pagBg1dKYzVpm6hzD/form"
								frameborder="0"
								width="100%"
								height="1000"
								style="background: transparent; border: 1px solid #ccc;"
							></iframe>
						</div>
					</Card.Content>
				</Card.Root>
			</div>
		</div>
	</div>
</div>
